import React from "react";
import '../../styles/components/settings/settings-section.scss';
import SelectWithVisibleOptions from "../common/SelectWithVisibleOptions";
import settingsPage from "../../services/settings/settingsPage";
import RangeInput from "../common/RangeInput";
import ControlGroup, {ControlGroupItem} from "./ControlGroup";
import Button from "../common/Button";
import {useKeyboardAndVolumesPageLogic} from "./KeyboardAndVolumesPage.logic";
import settingsService from "../../services/settings/settingsService";
import {useNavigate} from "react-router-dom";
import {useAppSelector} from "../../redux/hooks";
import {settingsStore} from "../../redux/settings/settingsStore";
import ISelectOption from "../../models/view/common/ISelectOption";
import {keyboardKeyCounts} from "../../redux/settingsPage/settingsPageTypes";
import KeyboardWizardModal from "../onboarding/keyboardWizard/KeyboardWizardModal";
import {keyboardWizardStore} from "../../redux/keyboardWizard/keyboardWizardStore";
import {appPagePaths} from "../../config/config";

interface IKeyboardAndVolumesProps{}

export default function KeyboardAndVolumes({}: IKeyboardAndVolumesProps) {
  const {midiDevice: {currentlySelectedMidiDevice}} = useKeyboardAndVolumesPageLogic();
  const navigate = useNavigate();

  const midiDeviceOptions = useAppSelector(() => { return settingsStore.getMidiDeviceOptions().getSelectOptions(); });
  const pianoVolume = useAppSelector(settingsStore.getPianoVolume);
  const backingTrackVolume = useAppSelector(settingsStore.getBackingTrackVolume);
  const videoVolume = useAppSelector(settingsStore.getVideoVolume);
  const metronomeVolume = useAppSelector(settingsStore.getMetronomeVolume);
  const countInVolume = useAppSelector(settingsStore.getCountInVolume);
  const virtualPianoNumberOfKeys = useAppSelector(settingsStore.getVirtualPianoNumberOfKeys);

  const numberOfKeysOptions = createKeyboardNumberOfKeysSelectOptions();

  return (
    <div className="settings-section keyboard-and-volumes">
      <header>Keyboard & Volumes</header>
      <main className="settings">
        <SelectWithVisibleOptions
          label={"MIDI Input Devices"}
          onChange={(id, name) => {
            settingsService.setCurrentlySelectedMidiDevice(id, name);
          }}
          options={midiDeviceOptions}
          value={currentlySelectedMidiDevice}
        />
        <Button
          text={`Configure Keyboard`}
          onClick={() => {
            keyboardWizardStore.reset();
            keyboardWizardStore.setIsOpen(true);
          }}
          buttonType={"secondary"}
          minWidth={440}
        />
        <SelectWithVisibleOptions
          label={"Onscreen Key Count"}
          layout="horizontal"
          additionalClassName="keyboardSizes"
          onChange={(count) => {
            if(typeof count === "number") {
              settingsPage.changeVirtualPianoNumberOfKeys(count);
            }
          }}
          options={numberOfKeysOptions}
          value={virtualPianoNumberOfKeys}
        />

        <ControlGroup label="Volume Levels">
          <ControlGroupItem>
            <RangeInput
              label={"Piano Volume"}
              secondaryLabel={`${Math.ceil(pianoVolume * 100)}%`}
              onChange={(value: number) => {
                settingsStore.setPianoVolume(value / 100);
              }}
              value={pianoVolume * 100}
              min={0}
              max={100}
              step={1}
            />
          </ControlGroupItem>
          <ControlGroupItem>
            <RangeInput
              label={"Backing Track Volume"}
              secondaryLabel={`${Math.ceil(backingTrackVolume * 100)}%`}
              onChange={(value: number) => {
                settingsStore.setBackingTrackVolume(value / 100);
              }}
              value={backingTrackVolume * 100}
              min={0}
              max={100}
              step={1}
            />
          </ControlGroupItem>
          <ControlGroupItem>
            <RangeInput
              label={"Video Volume"}
              secondaryLabel={`${Math.ceil(videoVolume * 100)}%`}
              min={0} 
              max={100} 
              value={videoVolume * 100}
              onChange={(value: number) => {
                settingsStore.setVideoVolume(value / 100);
              }} 
            />
          </ControlGroupItem>
          <ControlGroupItem>
            <RangeInput
              label={"Metronome Volume"}
              secondaryLabel={`${Math.ceil(metronomeVolume * 100)}%`}
              onChange={(value: number) => {
                settingsStore.setMetronomeVolume(value / 100);
              }}
              value={metronomeVolume * 100}
              min={0}
              max={100}
              step={1}
            />
          </ControlGroupItem>
          <ControlGroupItem>
            <RangeInput
              label={"Count In Volume"}
              secondaryLabel={`${Math.ceil(countInVolume * 100)}%`}
              onChange={(value: number) => {
                settingsStore.setCountInVolume(value / 100);
              }}
              value={countInVolume * 100}
              min={0}
              max={100}
              step={1}
            />
          </ControlGroupItem>
        </ControlGroup>

        <Button
          text="Reset Volumes to Default"
          ariaLabel={"Reset All Volume Levels to Default"}
          buttonType={"secondary"}
          minWidth={440}
          onClick={() => {
            settingsStore.resetVolumes();
          }}/>
      </main>
      <KeyboardWizardModal onStartPlaying={() => { navigate(appPagePaths.bootCampPage); }} />
    </div>
  );
}

function createKeyboardNumberOfKeysSelectOptions(){
  const customSelectOption: ISelectOption<number | string> = { label: "Custom", value: "Custom" };
  const selectOptions = keyboardKeyCounts.map(count => {
    const selectOption: ISelectOption<number | string> = {
      value: count,
      label: count.toString(),
    };
    return selectOption;
  });
  return [customSelectOption].concat(selectOptions);
}